import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { RoamingSettingsService } from "../services";
import { logger } from "../utils";
import { Token } from "../models/authentication";
import { AccountInfo } from "@azure/msal-browser";

type TokenState = {
  token: Token | undefined;
  id_token: string | undefined;
  msalToken: string | undefined;
  msalAccount: AccountInfo | undefined;
  isAuthenticated: boolean;
  isExpired: boolean;
  tokenRefreshed: boolean;
};

type TokenActions = {
  setToken: (token: Token) => void;
  setMsalToken: (msalToken: string) => void;
  setMsalAccount: (msalAccount: AccountInfo) => void;
  setAuthenticated: (isAuthenticated: boolean) => void;
  setExpired: (isExpired: boolean) => void;
  isTokenExpired: () => boolean;
  setTokenRefreshed: (tokenRefreshed: boolean) => void;
  removeToken: () => void;
  removeMsalData: () => void;
  resetState: () => void;
};

const initialState: TokenState = {
  token: undefined,
  id_token: undefined,
  msalToken: undefined,
  msalAccount: undefined,
  isAuthenticated: false,
  isExpired: false,
  tokenRefreshed: false,
};

/**
 * Used internally to validate that the token is not expired.
 *
 * @param idToken - The id_token to check
 * @param bufferTime - The buffer time in milliseconds to check for expiration. Defaults to 1 hour.
 *
 * @returns {boolean} - True if the token is expired, false otherwise
 */
const checkIfTokenExpired = (idToken: string, bufferTime = 60 * 60 * 1000): boolean => {
  try {
    const base64Url = idToken.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const tokenPayload = JSON.parse(jsonPayload);
    const expiryTime = tokenPayload.exp * 1000;
    const currentTime = Date.now();

    return currentTime >= expiryTime - bufferTime;
  } catch (e) {
    logger.error("Error checking token expiry", e, bufferTime, idToken);
    return true;
  }
};

// init roaming settings service
export const cadchatTokenStorage = new RoamingSettingsService("cadchat-token-storage");

// create the token store
export const useTokenStore = create<TokenState & TokenActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,
        setToken: (token: Token) => {
          set({
            token,
            id_token: token?.id_token,
            isAuthenticated:
              token && token?.id_token ? !checkIfTokenExpired(token?.id_token, 0) : false,
            isExpired: token && token?.id_token ? checkIfTokenExpired(token?.id_token) : true,
          });
        },
        setMsalToken: (msalToken) => set({ msalToken }),
        setMsalAccount: (msalAccount) => set({ msalAccount }),
        setAuthenticated: (isAuthenticated: boolean) => set({ isAuthenticated }),
        setExpired: (isExpired: boolean) => set({ isExpired }),
        setTokenRefreshed: (tokenRefreshed: boolean) => set({ tokenRefreshed }),
        isTokenExpired: () => {
          // get the current token
          const token = get().token;
          if (!token || !token?.id_token) return true;

          // check to see if it's really expired
          const isExpired = checkIfTokenExpired(token?.id_token) ?? false;

          // update the current state
          set({ isExpired });

          // return the result from the expiry check
          return isExpired;
        },
        removeToken: () =>
          set({
            token: undefined,
            id_token: undefined,
            isAuthenticated: false,
            isExpired: true,
            tokenRefreshed: false,
          }),
        removeMsalData: () => set({ msalToken: undefined, msalAccount: undefined }),
        resetState: () => {
          if (cadchatTokenStorage.isRoamingAvailable()) {
            cadchatTokenStorage.clear();
          }
          set(initialState);
        },
      }),
      {
        name: "token-storage",
        storage: createJSONStorage(() => cadchatTokenStorage),
        partialize: (state) => ({
          token: state.token,
          id_token: state.id_token ?? state.token?.id_token,
          isAuthenticated:
            state.token && state.token?.id_token
              ? !checkIfTokenExpired(state.token?.id_token, 0)
              : false,
          isExpired:
            state.token && state.token?.id_token
              ? checkIfTokenExpired(state.token?.id_token)
              : true,
          msalToken: state.msalToken,
          msalAccount: state.msalAccount,
        }),
        skipHydration: true,
        // used for debugging
        // onRehydrateStorage: () => (state, error) => {
        //   if (error) {
        //     logger.error("Error during token rehydration:", error);
        //   } else {
        //     state?.setHasHydrated(true);
        //     logger.info("Applying rehydrated token state:", state);
        //   }
        // },
      }
    )
  )
);

// rehydrate the state on Office ready
Office.onReady(async () => {
  if (cadchatTokenStorage.isRoamingAvailable()) {
    await useTokenStore.persist.rehydrate();
  }
});
